import { Fragment, useCallback, useEffect, useState } from 'react';
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Bar,
} from 'recharts';
import CustomTooltip from './components/customTooltip/CustomTooltip';
import './chart.css';
import { useNavigate } from 'react-router-dom';
import { PR_CHART_KEYS } from '../shared/utils';
import MetricSidebar from './components/MetricSidebar';
import moment from 'moment';
import { useTheme } from '@chakra-ui/react';
import CustomLegend from '../customLegend';
import { DateRangeSelection } from '../shared/typings';

export interface HrComboChartDataItem {
  contributor: string;
  prs_with_feedback: string;
  percentage_prs_reviewed: string;
  prs_without_feedback: string;
  prs_reviewed?: number;
  startDate?: string;
  endDate?: string;
  [key: string]: string | number | undefined;
}

export interface HrComboChartKey {
  key: string;
  name: string;
  color: string;
  chartType: string;
}

interface ComboChartRechartsProps {
  chartref?: any;
  data: HrComboChartDataItem[];
  keys?: HrComboChartKey[];
  chartMetadata?: any;
  showLabel?: boolean;
  selectedTeam: string;
  hideBtn?: boolean;
  slant?: boolean;
  forDetails?: boolean;
  ticketType?: string;
  selected?: DateRangeSelection;
  repos?: string[];
}
// THis component is used to show hr combo chart in Reviewer Distribution
export const HrComboChartRecharts = ({
  chartref,
  data,
  keys,
  chartMetadata,
  showLabel,
  selectedTeam,
  hideBtn,
  slant,
  forDetails,
  ticketType,
  selected,
  repos,
}: ComboChartRechartsProps) => {
  const { colors } = useTheme();
  const [hoveredLabel, setHoveredLabel] = useState<any>(null);
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [clickedData, setClickedData] = useState<any>(null);
  const [currentChartMeta, setCurrentChartMeta] = useState<any>(null);
  const [barProps, setBarProps] = useState<any>({});
  const [hoveredBar, setHoveredBar] = useState<{
    key: string;
    index: number;
  } | null>(null);

  useEffect(() => {
    setBarProps(
      keys?.reduce(
        (a: any, { key }: HrComboChartKey) => {
          a[key] = true;
          return a;
        },
        {
          hover: null,
        }
      )
    );
  }, [keys]);

  const handleModalClose = useCallback(() => {
    setIsOpen(false);
  }, [isOpen]);

  const handleModalOpen = useCallback(() => {
    setIsOpen(true);
  }, [isOpen]);

  const RenderCustomTick = (props: any) => {
    const { x, y, payload } = props;
    const { value } = payload;

    return props?.chartMetaData?.chartKey === 'TOP_REVIEWER_DISTRIBUTION' ? (
      <g transform={`translate(${x},${y})`}>
        <text
          dy={2}
          fill={colors.primary}
          fontSize={12}
          fontStyle={'Inter'}
          cursor="pointer"
          textAnchor="end"
          textDecoration={'underline'}
          onClick={(e) => {
            if (e.ctrlKey || e.metaKey) {
              e.preventDefault();
              window.open(`/contributors/${value}`, '_blank');
            } else {
              navigate(`/contributors/${value}`);
            }
          }}
        >
          {value}
        </text>
      </g>
    ) : (
      <g transform={`translate(${x},${y})`}>
        <text dy={2} fontSize={11} textAnchor="end">
          {value}
        </text>
      </g>
    );
  };

  const newKeys = keys?.sort((a, b) => b.key.localeCompare(a.key));

  const prsReviewedMax = Math.max(
    ...(data?.map((entry) => entry.prs_reviewed || 0) || [0])
  );

  const xDomain = [0, prsReviewedMax];

  return (
    <>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          ref={chartref}
          data={data}
          margin={{
            top: 10,
            right: 35,
            left: 240,
            bottom: forDetails ? 0 : -45,
          }}
          layout="vertical"
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            type="number"
            xAxisId={'bottom'}
            orientation="bottom"
            domain={[0, 100]}
            tickLine={false}
            tickFormatter={(value) => `${value}%`}
            style={{
              fontSize: 11,
              fontWeight: 500,
            }}
          />
          <XAxis
            type="number"
            tickLine={false}
            orientation="top"
            domain={xDomain}
            style={{
              fontSize: 11,
              fontWeight: 500,
            }}
            label={{
              value: 'PRs Reviewed',
              position: 'center',
              fontSize: 11,
              fontWeight: 500,
              dy: -15,
            }}
          />

          <YAxis
            tickLine={false}
            label={{
              value: chartMetadata.ylabel,
              angle: -90,
              position: 'insideTopLeft',
              fontSize: 11,
              fontWeight: 500,
              dx: -240,
              dy: 110,
            }}
            style={{
              fontSize: 11,
              fontWeight: 600,
            }}
            dataKey={chartMetadata.yAxis}
            tick={<RenderCustomTick chartMetaData={chartMetadata} />}
            interval={0}
            minTickGap={5}
            type="category"
          />
          <Tooltip
            content={
              <CustomTooltip
                handleModalOpen={handleModalOpen}
                setClickedData={setClickedData}
                unit={chartMetadata.zlabel}
                onClickAction={() => {
                  setCurrentChartMeta(chartMetadata);
                }}
                hideBtn={true}
              />
            }
            wrapperStyle={{ outline: 'none' }}
            position={{ x: 100 }}
            allowEscapeViewBox={{ x: false, y: false }}
          />

          {showLabel && (
            <Legend
              verticalAlign="top"
              layout="vertical"
              iconType="circle"
              iconSize={11}
              align="right"
              content={
                <CustomLegend
                  keys={keys?.filter((el) => el.key !== 'prs_reviewed') || []}
                  barProps={barProps}
                  setBarProps={setBarProps}
                />
              }
              wrapperStyle={{
                right: -8,
                fontSize: 11.5,
                top: 50,
              }}
            />
          )}

          {newKeys?.map((el, index: number) =>
            el.key === 'yRollingAvg' ? (
              <Line
                key={index}
                type="monotone"
                dataKey={el.key}
                name={el.name}
                stroke={el.color}
                dot={false}
                activeDot={false}
                strokeWidth={2}
                strokeDasharray="3 6"
              />
            ) : el.chartType === 'line_chart' ? (
              <Line
                key={index}
                xAxisId={'bottom'}
                dataKey={el.key}
                name={el.name}
                stroke={el.color}
                strokeWidth={2}
                opacity={barProps.hover && barProps.hover !== el.key ? 0.25 : 1}
                hide={barProps[el.key as string] === false}
                dot={{
                  fill: el.color,
                  stroke: el.color,
                  strokeWidth: 3,
                  r: 0,
                }}
              />
            ) : el.chartType === 'bar_chart' && el.key !== 'prs_reviewed' ? (
              // excludes the prs_reviewed bar chart.
              <Bar
                key={index}
                dataKey={el.key}
                name={el.name}
                barSize={20}
                fill={el.color}
                opacity={barProps.hover && barProps.hover !== el.key ? 0.25 : 1}
                hide={barProps[el.key as string] === false}
                stackId={'a'}
                style={{ cursor: 'pointer' }}
                onMouseEnter={(_, barIndex) =>
                  setHoveredBar({ key: el.key, index: barIndex })
                }
                onMouseLeave={() => setHoveredBar(null)}
                onClick={(event: any, payload: any) => {
                  handleModalOpen();
                  setClickedData({
                    name: event.contributor,
                    payload: {
                      startDate: event.startDate,
                      endDate: event.endDate,
                      clickedKey: el.key,
                      clickedKeyName: el.name || '',
                    },
                  });

                  setCurrentChartMeta(chartMetadata);
                }}
              />
            ) : (
              <Fragment key={index}></Fragment>
            )
          )}
        </ComposedChart>
      </ResponsiveContainer>
      {[...PR_CHART_KEYS].includes(currentChartMeta?.chartKey) && isOpen && (
        <MetricSidebar
          openDrawer={isOpen}
          handleDrawerClose={handleModalClose}
          heading={
            currentChartMeta?.chartKey !== 'TOP_REVIEWER_DISTRIBUTION'
              ? chartMetadata.chartTitle
              : clickedData.payload.clickedKeyName
          }
          subHeading={`${moment(selected?.startDate).format(
            'DD MMM'
          )} - ${moment(selected?.endDate).format('DD MMM')}, ${
            clickedData?.name
          }`}
          payload={{
            ...clickedData,
            payload: {
              startDate: selected?.startDate,
              endDate: selected?.endDate,
            },
          }}
          currentChartMeta={currentChartMeta}
          filterBy={
            currentChartMeta?.chartKey === 'TOP_REVIEWER_DISTRIBUTION'
              ? {
                  author: clickedData?.name?.toLowerCase(),
                  projectsFilter: repos,
                  teamId: selectedTeam,
                  sprintId: selected?.sprintId,
                  prViewFilters: {
                    isPrReviewedWithComment:
                      clickedData?.payload?.clickedKey === 'prs_with_feedback'
                        ? true
                        : false,
                  },
                }
              : {
                  author: clickedData?.name?.toLowerCase(),
                  projectsFilter: repos,
                  teamId: selectedTeam,
                  sprintId: selected?.sprintId,
                }
          }
          selectedTeam={selectedTeam}
        />
      )}
    </>
  );
};
